import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Question, QuestionResponse, Section } from '../../../../interfaces/questions.interface';
import { MultipleSelectionOptions, MultipleSelectionQuestion } from '../../../../interfaces/multiple-selection.question';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { ConditionalLogicTargetQuestions, SingleChoiceOption } from '../../../../interfaces/single-selection-question.interface';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FormGroupExt } from '../../../../../../form-ext/form-group.ext';
import { FormBuilderExt } from '../../../../../../form-ext/form-builder.ext';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { CifcReview } from '../../../../interfaces/cifc.interface';
import * as _ from 'lodash';

@Component({
    selector: 'app-multiple-selection',
    templateUrl: './multiple-selection.component.html',
    styleUrls: ['./multiple-selection.component.scss'],
    standalone: false,
})
export class MultipleSelectionComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
    @Input() question: Question;
    @Input() questionIndex: number;

    @ViewChild('titleTextarea') titleTextarea: ElementRef;
    public panelOpenState: boolean[];
    public conditionalQuestions: Section[] = [];

    public questionTitle: string;
    public questionPlaceholder: string;
    public descriptionPlaceholder = $localize`Description (optional)`;
    public answerInputPlaceholder = $localize`Type option here`;
    public MIN_ROWS = 2;
    public multipleForm: FormGroupExt;
    public optionObj: MultipleSelectionOptions = {
        id: '',
        text: '',
        checked: false,
        is_other_option: false,
    };

    public questionBody: MultipleSelectionQuestion;
    public currentAnswer: MultipleSelectionOptions[];
    public questionReview: CifcReview;

    public subscription: Subscription;
    public questionSubscription: Subscription;
    public reviewSubscription: Subscription;
    public questionChangedOnReviewReset: Subscription;
    public showErrorSubscription: Subscription;

    public panelTarget: Element | null;
    public panelTargetRect: DOMRect | null;

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cdr: ChangeDetectorRef,
        public formBuilderExt: FormBuilderExt,
    ) {
        this.subscription = this.customerQuestionnaireService.onQuestionMarked().subscribe((question: Question) => {
            this.questionBody.options = this.currentAnswer;
        });

        if (this.commonQuestionnaireService.createMode) {
            this.questionSubscription = this.cstQuestionnaireService.getQuestionObs().subscribe((question) => {
                this.cstQuestionnaireService.buildQuestion(this, question);
            });
            this.showErrorSubscription = this.cstQuestionnaireService.showErrorOnForm.asObservable().subscribe((val) => {
                this.TitleControl?.markAsTouched();
                this.cdr.detectChanges();
            });
        }
    }

    public get TitleControl(): AbstractControl | null {
        return this.multipleForm?.group.get('title');
    }

    public get DescriptionControl(): AbstractControl | null {
        return this.multipleForm?.group.get('description');
    }

    ngOnInit() {
        this.panelOpenState = new Array(this.questionBody?.options.length).fill(false);
        this.conditionalQuestions = this.cstQuestionnaireService.getConditionalQuestions();
        this.conditionalQuestions = this.cstQuestionnaireService.getConditionalQuestions();

        this.reviewSubscription = this.commonQuestionnaireService.reviewAddedObs().subscribe((review: CifcReview) => {
            if (review?.question_id === this.question.id && review.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                this.questionReview = review;
            }
        });

        if (this.commonQuestionnaireService.reviewMode) {
            this.questionChangedOnReviewReset = this.commonQuestionnaireService
                .questionChangedOnReviewResetObs()
                .subscribe((question: Question) => {
                    if (question.id === this.question.id && question.body.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                        this.currentAnswer = question.body.options as MultipleSelectionOptions[];
                        (this.questionReview as any) = _.cloneDeep(
                            this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === this.question.id)?.cifc_review,
                        );
                        if (this.questionReview?.solved) {
                            (this.questionReview as any) = null;
                        }
                    }
                });
        }

        this.multipleForm = this.formBuilderExt.groupExt(
            {
                title: [this.question.title, [Validators.required]],
                description: [this.question.description],
            },
            {},
            {
                title: {
                    required: $localize`Please enter a title for this question`,
                },
            },
        );
        this.addOptionToCurrentForm();
        this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.question && changes.question.currentValue !== changes.question.previousValue) {
            this.question = changes.question.currentValue;
            this.TitleControl?.setValue(this.question.title);
            this.DescriptionControl?.setValue(this.question.description);
            this.putOtherOptionLast();
            this.cstQuestionnaireService.buildQuestion(this, changes.question.currentValue);
            this.panelOpenState = new Array(this.questionBody?.options.length).fill(false);
            this.conditionalQuestions = this.cstQuestionnaireService.getConditionalQuestions();
            this.addOptionToCurrentForm();
            this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.questionSubscription?.unsubscribe();
        this.reviewSubscription?.unsubscribe();
        this.questionChangedOnReviewReset?.unsubscribe();
        this.showErrorSubscription?.unsubscribe();
        this.cdr.detach();
    }

    public onSelectionChanged(option: MultipleSelectionOptions) {
        if (this.currentAnswer.filter((x) => x.checked).length > 0) {
            const getOtherOp = this.currentAnswer.filter((x) => x.checked && x.is_other_option);
            if (getOtherOp.length === 0 || (getOtherOp.length > 0 && getOtherOp[0].text !== '')) {
                this.question.answerChanged = true;
            } else if (getOtherOp.length > 0 && getOtherOp[0].text === '') {
                this.question.answerChanged = false;
            }
        } else {
            this.question.answerChanged = false;
        }
        this.commonQuestionnaireService.onSelectionChangeMultiple(option, this.currentAnswer);
    }

    public hasAtLeastOneAnswer() {
        return (
            ((this.currentAnswer.filter((x) => x.checked && !x.is_other_option).length > 0 ||
                (this.currentAnswer?.find((x) => x.is_other_option) &&
                    this.currentAnswer?.find((x) => x.is_other_option)?.checked &&
                    this.currentAnswer?.find((x) => x.is_other_option)?.text !== '')) &&
                this.question.answerChanged) ||
            (this.question.cifc_review && !this.question.cifc_review.solved) ||
            (this.question as unknown as QuestionResponse).body.deleted
        );
    }

    public onQuestionMarked($event: boolean) {
        if (this.currentAnswer.filter((x) => x.checked).length > 0) {
            this.question.answerChanged = !$event;
        }
    }

    public addOptionToCurrentForm() {
        this.currentAnswer?.forEach((option: SingleChoiceOption, index: number) => {
            option.checked && this.commonQuestionnaireService.onSelectionChangeMultiple(option, this.currentAnswer);
            if (this.multipleForm && !this.multipleForm?.group.contains('option_' + index)) {
                if (!option.is_other_option) {
                    const errorName = 'option_' + index;
                    this.multipleForm?.group.addControl('option_' + index, new UntypedFormControl(option.text || '', Validators.required));
                    this.multipleForm?.addError(errorName, {
                        required: 'You need to write a label for this option',
                    });
                } else {
                    this.multipleForm?.group.addControl('option_' + index, new UntypedFormControl(option.text || ''));
                }
            } else {
                this.getOptionControl(index)?.setValue(option.text);
            }
        });
    }

    public putOtherOptionLast() {
        this.currentAnswer?.push(
            this.currentAnswer.splice(
                this.currentAnswer.findIndex((x) => x.is_other_option),
                1,
            )[0],
        );
        /* We need to delete all controls and rebuild them due to the fact that the option with otherOption
         * Needs to be last and to have no validators on it */
        this.currentAnswer?.forEach((op, index) => {
            this.multipleForm.group.removeControl('option_' + index);
        });
    }

    public onDeleteOption(index: number) {
        if (index < this.currentAnswer.length - 1) {
            this.currentAnswer.forEach((op, index) => {
                this.multipleForm.group.removeControl('option_' + index);
            });
            this.addOptionToCurrentForm();
        } else {
            this.multipleForm.group.removeControl('option_' + index);
        }
        this.cstQuestionnaireService.deleteQuestionField(this.currentAnswer, index, this.MIN_ROWS);
        this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
    }

    public addNewOption() {
        this.cstQuestionnaireService.addQuestionField(
            this.currentAnswer,
            this.cstQuestionnaireService.getObjectWithRandomId(this.optionObj),
        );
        this.putOtherOptionLast();
        this.addOptionToCurrentForm();
        this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
    }

    public onOptionAdded(option: SingleChoiceOption, i: number) {
        this.getOptionControl(i)?.setValue(option.text);
        this.currentAnswer[i].text = this.getOptionControl(i)?.value;
        this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
    }

    public getOptionControl(i: number): AbstractControl | null {
        return this.multipleForm?.group.get('option_' + i);
    }

    public onTitleChange() {
        this.question.title = this.TitleControl?.value;
    }

    public onDescriptionChange() {
        this.question.description = this.DescriptionControl?.value;
    }

    public changePanelOpenDisplay(i: number) {
        this.panelOpenState[i] = !this.panelOpenState[i];
    }

    public openConditionalPanel(i: number, $event: Event): void {
        this.panelTarget = ($event.target || $event.currentTarget) as Element;
        this.panelTargetRect = this.panelTarget.getClientRects()[0] ?? null;

        this.panelOpenState[i] = true;
    }

    public markAllConditionalQuestionsBySection($event: MatCheckboxChange, option: SingleChoiceOption, section: Section) {
        if (!option.target_question_list) {
            option.target_question_list = [];
        }

        option.target_question_list = option.target_question_list.filter(
            (item: ConditionalLogicTargetQuestions) => item.section_id !== section.id,
        );

        section.questions.forEach((question: Question) => {
            if (question.id && $event.checked) {
                option.target_question_list?.push({
                    question_id: question.id,
                    section_id: section.id,
                    checked: true,
                });
            }
        });
    }

    public markQuestionForConditionalLogic($event: MatCheckboxChange, section: Section, question: Question, option: SingleChoiceOption) {
        if (!option.target_question_list) {
            option.target_question_list = [];
        }

        if (question.id && $event.checked) {
            option.target_question_list.push({
                question_id: question.id,
                section_id: section.id,
                checked: true,
            });
        }

        if (!$event.checked) {
            option.target_question_list.splice(
                option.target_question_list.findIndex(
                    (item: ConditionalLogicTargetQuestions) => item.section_id === section.id && item.question_id === question.id,
                ),
                1,
            );
        }
    }

    public checkQuestionIsConditional(option: SingleChoiceOption, section: Section, question: Question): boolean {
        if (!option.target_question_list?.filter((item: ConditionalLogicTargetQuestions) => item.section_id === section.id).length) {
            return false;
        }

        return (
            option.target_question_list?.findIndex(
                (item: ConditionalLogicTargetQuestions) =>
                    item.section_id === section.id && item.question_id === question.id && item.checked,
            ) !== -1
        );
    }

    public checkIndeterminateState(option: SingleChoiceOption, section: Section): boolean {
        const conditionalSectionsLength = option.target_question_list?.filter(
            (item: ConditionalLogicTargetQuestions) => item.section_id === section.id,
        ).length;

        if (!conditionalSectionsLength) {
            return false;
        }

        return section.questions.length !== conditionalSectionsLength;
    }

    public checkAllSelections(option: SingleChoiceOption, section: Section): boolean {
        return (
            section.questions.length ===
            option.target_question_list?.filter((item: ConditionalLogicTargetQuestions) => item.section_id === section.id).length
        );
    }

    public checkOtherOption($event: MatCheckboxChange) {
        if (!$event.checked) {
            this.multipleForm.group.removeControl('option_' + (this.currentAnswer.length - 1));
        }
        this.cstQuestionnaireService.onOtherOptionChange($event, this.currentAnswer);
        this.cstQuestionnaireService.currentQuestionForm = this.multipleForm;
    }

    ngAfterViewInit(): void {
        if (this.titleTextarea) {
            this.titleTextarea.nativeElement.style.height = this.titleTextarea.nativeElement.scrollHeight + 'px';
        }
    }

    @HostListener('window:resize', ['$event'])
    private onResize() {
        // on resize recalculate the position of the open panel
        this.panelTargetRect = this.panelTarget?.getClientRects()[0] ?? null;
    }
}
